import type {
    CalendarTerminNoteDto,
    VorgangTerminDto,
} from "../../../services/calendarTerminService";
import type { DocumentDto } from "../../../services/documentService";
import type { OpenTerminFormParams } from "../../windowManager/openTerminFormWindow";
import { sortTermineByStart } from "./vorgangTermine";

/**
 * Reine Helfer für die „Termin-Notizen"-Übersicht im Notizen-Reiter (D3, Review
 * 2026-07-06). Bewusst ohne React/DOM, damit sie zeitzonen-robust unit-testbar
 * bleiben — analog zu {@link ./vorgangTermine}.
 *
 * D3: Im Notizen-Reiter des Vorgangs werden zusätzlich die Notizen der mit dem
 * Vorgang verknüpften Termine (die eine Notiz haben) als Übersicht mit Absprung
 * gezeigt; Dokumente aus solchen Terminen fließen in den Dokumente-Reiter ein.
 */

/** Eine Zeile der Übersicht: genau eine Termin-Notiz, angereichert um den Termin. */
export interface TerminNotizRow {
    termin: VorgangTerminDto;
    note: CalendarTerminNoteDto;
}

/** Zeitstempel einer Termin-Notiz als ms seit Epoch; null/ungültig → NaN. */
export function noteTimeMs(n: Pick<CalendarTerminNoteDto, "timestamp">): number {
    if (!n.timestamp) return Number.NaN;
    const t = new Date(n.timestamp).getTime();
    return Number.isNaN(t) ? Number.NaN : t;
}

/**
 * Baut die flache „Termin-Notizen"-Übersicht: je Termin-Notiz eine Zeile. Termine
 * ohne Notiz fallen raus (keine Filter-UI nötig — implizit über die Vorgang↔
 * Termin-Verknüpfung). Sortierung: Termine chronologisch (neueste/kommende
 * zuerst, wie im Termine-Reiter), je Termin die Notizen neueste zuerst.
 */
export function buildTerminNotizRows(
    termine: VorgangTerminDto[],
    notesByTermin: Record<number, CalendarTerminNoteDto[]>,
): TerminNotizRow[] {
    const rows: TerminNotizRow[] = [];
    for (const termin of sortTermineByStart(termine)) {
        const notes = notesByTermin[termin.terminId] ?? [];
        if (notes.length === 0) continue;
        const sorted = [...notes].sort((a, b) => {
            const am = noteTimeMs(a);
            const bm = noteTimeMs(b);
            const aHas = !Number.isNaN(am);
            const bHas = !Number.isNaN(bm);
            if (aHas && bHas) return bm - am;
            if (aHas) return -1;
            if (bHas) return 1;
            return b.id - a.id;
        });
        for (const note of sorted) rows.push({ termin, note });
    }
    return rows;
}

/**
 * Parameter für den „Absprung" (Absprung = zum Termin springen): öffnet das
 * Termin-Fenster im Edit-Modus über (mailbox, graphEventId). Betreff/Zeit werden
 * als Prefill mitgegeben; das Fenster lädt den vollen Termin bei Bedarf nach.
 */
export function terminNotizJumpParams(t: VorgangTerminDto): OpenTerminFormParams {
    const start = t.start ? new Date(t.start) : new Date();
    const end = t.end ? new Date(t.end) : start;
    return {
        editEventId: t.graphEventId,
        mailbox: t.mailbox,
        canEdit: true,
        prefillEvent: {
            id: t.graphEventId,
            title: t.subject ?? "Termin",
            start,
            end,
            allDay: t.isAllDay,
            location: "",
            mailbox: t.mailbox,
        },
    };
}

/**
 * Stammt ein Dokument aus einem Termin-Sidecar (und NICHT direkt vom Vorgang/
 * Todo)? Solche Dokumente werden im Dokumente-Reiter des Vorgangs nur
 * eingeblendet (Download), aber NICHT dort gelöscht — verwaltet werden sie am
 * Termin selbst (D3: „fließen zusätzlich ein").
 */
/**
 * Die Kategorie, mit der eine dauerhaft ABGELEGTE MAIL als Dokument liegt
 * (Cluster ABLAGE, 23.08.2026). Maschinell gesetzt, nie von Hand.
 */
export const KATEGORIE_MAIL_ARCHIV = "E-Mail-Archiv";

/**
 * Ist dieses „Dokument" in Wahrheit eine abgelegte Mail?
 *
 * PO 31.08.2026: „mails bitte nicht in dokumente anzeigen". Die abgelegte
 * `.eml` einer Mail ist technisch ein Dokument und liegt deshalb in derselben
 * Tabelle — in der Dokumentenliste eines Vorgangs oder To-dos hat sie aber
 * nichts verloren: dort sucht man Policen und Anschreiben, und der Betreff
 * einer Mail als Dateiname („2026-08-29_1615_Your weekly PIM digest…") schiebt
 * jede echte Datei aus dem Blick. Ihr Platz ist der Mails-Bereich.
 */
export function istAbgelegteMail(d: Pick<DocumentDto, "category">): boolean {
    return (d.category ?? "").trim().toLowerCase() === KATEGORIE_MAIL_ARCHIV.toLowerCase();
}

export function isTerminSourcedDoc(
    d: Pick<DocumentDto, "calendarTerminId" | "vorgangId" | "todoId">,
): boolean {
    return d.calendarTerminId != null && d.vorgangId == null && d.todoId == null;
}
